import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  acquireNotificationReads,
  ensureDueRead,
  useNotificationReadsStore,
} from '../store/useNotificationReadsStore';
import useSessionKey from './useSessionKey';

/**
 * Due-card count for the header badge, re-read on every navigation.
 *
 * Polled on purpose — do NOT move this to the WebSocket hub. `/api/ws` returns 404
 * for any user without a running or upcoming mocktest, and `Hub.Run` re-checks that
 * registration on every broadcast, so a review_due message would disconnect a
 * reviewing user rather than reach them. Due dates move over hours and days, so
 * reading on navigation is accurate enough.
 *
 * Reads `stats.dueToday` (due by end of today) rather than `due.counts` (due right
 * now), for two reasons: `getStats` is a read-only aggregation, while `getDue`
 * materializes admin templates as a side effect — not something the header should
 * trigger on every page load — and "vencem hoje" is the count the badge means.
 *
 * A contagem é **etiquetada com a sessão em que foi lida** (`useSessionKey`).
 * Antes, `enabled` e `location.pathname` eram as únicas dependências: uma troca
 * de usuário na mesma aba sem mudar de rota deixava o número do usuário
 * anterior na tela até a navegação seguinte. Agora a troca de token relê, e o
 * valor etiquetado com outra sessão nem chega a ser renderizado.
 *
 * A contagem e a leitura moram no `useNotificationReadsStore`, não num
 * `useState` daqui: este hook é chamado uma vez **por consumidor** de
 * `useNotifications` (o sino e o Sidebar, hoje), e com estado por montagem cada
 * navegação fazia duas `GET /review/stats` idênticas. O store dispara a leitura
 * uma vez por sessão/rota — a decisão de *qual* rota ler continua sendo desta
 * documentação.
 */
export default function useDueCount(enabled = true) {
  const session = useSessionKey();
  const location = useLocation();

  const read = useNotificationReadsStore((state) => state.due);
  const readSession = useNotificationReadsStore((state) => state.session);

  const active = enabled && !!session;

  // Enquanto alguém consome, o estado compartilhado vive; quando o último
  // desmonta, ele volta ao zero (nada de contagem sobrevivendo à árvore).
  useEffect(() => acquireNotificationReads(), []);

  useEffect(() => {
    if (!active || !session) return;
    ensureDueRead({ session, pathname: location.pathname });
  }, [active, session, location.pathname]);

  // Sem token, ou lida sob outra sessão: zero, e sem um frame intermediário
  // mostrando o número de quem estava logado antes.
  return active && readSession === session ? read : 0;
}
